import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../assets/css/header.css';
import { useTheme } from '../context/ThemeContext';
import msbWhite from '../assets/images/logos/msb-white.png';
import msbBlack from '../assets/images/logos/msb-black.png';
import googlePlayIcon from '../assets/images/icons/google-play.svg';
import HomeSectionLink from './HomeSectionLink';

const Header = () => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
    const [isMobileHowItWorksOpen, setIsMobileHowItWorksOpen] = useState(false);
    const [isSticky, setIsSticky] = useState(false);
    const [activeSection, setActiveSection] = useState('home');
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();


    // Scroll Spy Logic
    React.useEffect(() => {
        const handleScroll = () => {
            // Sticky Navbar Logic
            if (window.scrollY > 100) {
                setIsSticky(true);
            } else {
                setIsSticky(false);
            }

            // Scroll Spy Logic only on Home page
            if (location.pathname === '/') {
                const sections = [
                    { id: 'main-banner-area', name: 'home' },
                    { id: 'about', name: 'about' },
                    { id: 'our-process', name: 'our-process' },
                    { id: 'our-apps', name: 'our-apps' },
                    { id: 'benefits', name: 'benefits' },
                    { id: 'our-testimonial', name: 'our-testimonial' }
                ];

                // Check which section is in view
                // We add a small offset (e.g., 150px) to account for the fixed header height and trigger point
                const scrollCheck = window.scrollY + 150;

                let currentSection = 'home'; 
                let maxOffset = -1;

                // Stop scroll spy from drifting to other sections when at the very top
                if (window.scrollY < 200) {
                    currentSection = 'home';
                } else {
                    for (const section of sections) {
                        const element = document.getElementById(section.id);
                        if (element) {
                            const offset = element.offsetTop;
                            // Only update if this section is further down than the previously matched one
                            // this handles cases where multiple sections might be at offset 0 during load
                            if (scrollCheck >= offset && offset > maxOffset) {
                                currentSection = section.name;
                                maxOffset = offset;
                            }
                        }
                    }
                }
                setActiveSection(currentSection);
            } else {
                setActiveSection(''); // Reset on other pages
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        // Initial check
        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [location.pathname]);



    const closeDropdown = () => {
        setIsDropdownOpen(false);
    };

    const toggleSideMenu = () => {
        setIsSideMenuOpen(!isSideMenuOpen);
        if (!isSideMenuOpen) {
            document.body.classList.add('overflow-hidden');
        } else {
            document.body.classList.remove('overflow-hidden');
        }
    };

    const closeSideMenu = () => {
        setIsSideMenuOpen(false);
        document.body.classList.remove('overflow-hidden');
    };

    const handleScrollToTop = () => {
        // window.scrollTo({ top: 0, behavior: 'auto' }); // Handled by ScrollToTop component
    };

    // Helper to determine if a link is active
    const isActive = (sectionName) => {
        if (location.pathname !== '/') return '';
        return activeSection === sectionName ? 'active' : '';
    };

    return (
        <header className="site-header" id="header">
            <nav className={`navbar navbar-expand-xl transparent-bg static-nav ${isSticky ? 'fixedmenu' : ''}`}>
                <div className="container">
                    <Link className="navbar-brand" to="/">
                        <img src={msbWhite} alt="myskoolbus" className={`logo-default`} />
                        <img src={msbBlack} alt="myskoolbus" className={`logo-scrolled`} />
                    </Link>
                    <div className="collapse navbar-collapse">
                        <ul className="navbar-nav ml-auto align-items-center">
                            <li className="nav-item">
                                <HomeSectionLink className={`nav-link ${isActive('home')}`} section="main-banner-area">Home</HomeSectionLink>
                            </li>
                            <li className="nav-item">
                                <HomeSectionLink className={`nav-link ${isActive('about') || (location.pathname === '/about-us' ? 'active' : '')}`} section="about">About</HomeSectionLink>
                            </li>

                            <li
                                className={`nav-item dropdown position-relative ${isDropdownOpen ? 'show' : ''}`}
                                onMouseEnter={() => setIsDropdownOpen(true)}
                                onMouseLeave={() => setIsDropdownOpen(false)}
                            >
                                <HomeSectionLink
                                    className={`nav-link dropdown-toggle ${isActive('our-process') || (['/parents', '/schools'].includes(location.pathname) ? 'active' : '')}`}
                                    section="our-process"
                                    data-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded={isDropdownOpen}
                                    onClick={closeDropdown}
                                >
                                    How it works <i className="fas fa-chevron-down ml-1" style={{ fontSize: '0.9rem' }}></i>
                                </HomeSectionLink>
                                <div className={`dropdown-menu ${isDropdownOpen ? 'show' : ''}`}>
                                    <Link className="dropdown-item" to="/parents" onClick={() => { closeDropdown(); handleScrollToTop(); }}>Parents</Link>
                                    <Link className="dropdown-item" to="/schools" onClick={() => { closeDropdown(); handleScrollToTop(); }}>Schools</Link>
                                </div>
                            </li>
                            <li className="nav-item">
                                <HomeSectionLink className={`nav-link ${isActive('our-apps')}`} section="our-apps">MSB Apps</HomeSectionLink>
                            </li>
                            <li className="nav-item">
                                <HomeSectionLink className={`nav-link ${isActive('benefits')}`} section="benefits">Benefits</HomeSectionLink>
                            </li>
                            <li className="nav-item">
                                <HomeSectionLink className={`nav-link ${isActive('our-testimonial')}`} section="our-testimonial">Testimonials</HomeSectionLink>
                            </li>
                            <li className="nav-item">
                                <Link className={`nav-link ${location.pathname === '/packages' ? 'active' : ''}`} to="/packages" onClick={handleScrollToTop}>Packages</Link>
                            </li>
                            <li className="nav-item">
                                <Link className={`nav-link ${location.pathname === '/faq' ? 'active' : ''}`} to="/faq" onClick={handleScrollToTop}>FAQ</Link>
                            </li>
                            <li className="nav-item">
                                <Link className={`nav-link ${location.pathname === '/contact-us' ? 'active' : ''}`} to="/contact-us" onClick={handleScrollToTop}>Contact</Link>
                            </li>

                            {/* Icons Group Grouped in 1 Div and spaced from Contact */}
                            <li className="nav-item ml-xl-4">
                                <div className="navbar-controls-group d-flex align-items-center">
                                    <a
                                        href="https://play.google.com/store/apps/details?id=com.tracknow.myskoolbus"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="nav-icon-btn mr-3"
                                        title="Download on Google Play"
                                    >
                                        <img src={googlePlayIcon} alt="Google Play" />
                                    </a>

                                    <div className="theme-switch-container" title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}>
                                        <div className="theme-switch-label" onClick={toggleTheme}>
                                            <i className="fas fa-sun"></i>
                                            <i className="fas fa-moon"></i>
                                            <div className="theme-switch-ball">
                                                <i className="fas fa-bus"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>
            {/* side menu open button - MOVED OUTSIDE NAV */}
            <input id="checkbox2" type="checkbox" checked={isSideMenuOpen} onChange={toggleSideMenu} />
            <label className="toggle2 d-xl-none" htmlFor="checkbox2">
                <div className="bars" id="bar4"></div>
                <div className="bars" id="bar5"></div>
                <div className="bars" id="bar6"></div>
            </label>
            {/* side menu */}
            <div className={`side-menu bg-yellow ${isSideMenuOpen ? 'side-menu-active' : ''}`}>
                <div className="overlay" onClick={closeSideMenu}></div>
                <div className="inner-wrapper">
                    <div className="mobile-menu-logo">
                        <Link to="/" onClick={closeSideMenu}>
                            <img src={msbBlack} alt="myskoolbus" />
                        </Link>
                    </div>

                    <nav className="side-nav w-100">
                        <ul className="navbar-nav">
                            <li className="nav-item">
                                <HomeSectionLink className={`nav-link ${isActive('home')}`} section="main-banner-area" onClick={closeSideMenu}>Home</HomeSectionLink>
                            </li>
                            <li className="nav-item">
                                <HomeSectionLink className={`nav-link ${isActive('about') || (location.pathname === '/about-us' ? 'active' : '')}`} section="about" onClick={closeSideMenu}>About</HomeSectionLink>
                            </li>

                            <li className="nav-item">
                                <HomeSectionLink
                                    className={`nav-link collapsePagesSideMenu ${isActive('our-process') || (['/parents', '/schools'].includes(location.pathname) ? 'active' : '')}`}
                                    section="our-process"
                                    onClick={() => {
                                        setIsMobileHowItWorksOpen(!isMobileHowItWorksOpen);
                                    }}
                                >
                                    How it works <i className={`fas fa-chevron-${isMobileHowItWorksOpen ? 'up' : 'down'}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsMobileHowItWorksOpen(!isMobileHowItWorksOpen); }}></i>
                                </HomeSectionLink>
                                <div id="sideNavPages1" className={`collapse sideNavPages ${isMobileHowItWorksOpen ? 'show' : ''}`}>
                                    <ul className="navbar-nav mt-2">
                                        <li className="nav-item">
                                            <Link className="nav-link" to="/parents" onClick={() => { closeSideMenu(); handleScrollToTop(); }}>Parents</Link>
                                        </li>
                                        <li className="nav-item">
                                            <Link className="nav-link" to="/schools" onClick={() => { closeSideMenu(); handleScrollToTop(); }}>Schools</Link>
                                        </li>
                                    </ul>
                                </div>
                            </li>

                            <li className="nav-item">
                                <HomeSectionLink className={`nav-link ${isActive('our-apps')}`} section="our-apps" onClick={closeSideMenu}>msb Apps</HomeSectionLink>
                            </li>
                            <li className="nav-item">
                                <HomeSectionLink className={`nav-link ${isActive('benefits')}`} section="benefits" onClick={closeSideMenu}>Benefits</HomeSectionLink>
                            </li>
                            <li className="nav-item">
                                <HomeSectionLink className={`nav-link ${isActive('our-testimonial')}`} section="our-testimonial" onClick={closeSideMenu}>Testimonials</HomeSectionLink>
                            </li>
                            <li className="nav-item">
                                <Link className={`nav-link ${location.pathname === '/packages' ? 'active' : ''}`} to="/packages" onClick={() => { closeSideMenu(); handleScrollToTop(); }}>Packages</Link>
                            </li>
                            <li className="nav-item">
                                <Link className={`nav-link ${location.pathname === '/faq' ? 'active' : ''}`} to="/faq" onClick={() => { closeSideMenu(); handleScrollToTop(); }}>FAQ</Link>
                            </li>
                            <li className="nav-item">
                                <Link className={`nav-link ${location.pathname === '/contact-us' ? 'active' : ''}`} to="/contact-us" onClick={() => { closeSideMenu(); handleScrollToTop(); }}>Contact</Link>
                            </li>



                        </ul>
                    </nav>

                </div>
            </div>
            <div
                id="close_side_menu"
                className="tooltip"
                onClick={closeSideMenu}
                style={{ display: isSideMenuOpen ? 'block' : 'none', opacity: isSideMenuOpen ? 0.5 : 0 }}
            >
            </div>
            {/* End side menu */}
        </header>
    );
};

export default Header;
